import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
  INVOICE_COMPANY,
  buildInvoiceLineItems,
  computeDueDate,
  formatAmount,
  formatShortDate,
  resolveInvoiceNumber,
} from './invoiceHelpers';

/**
 * Generate and download a PDF invoice matching the VPS template layout.
 */
export function downloadReceiptPdf(payment, filename = 'vps-invoice.pdf') {
  if (!payment) return false;

  const doc = new jsPDF({ unit: 'pt', format: 'letter' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const margin = 48;
  let y = margin;

  const isPaid = String(payment.status || '').toLowerCase() === 'completed';
  const invoiceNumber = resolveInvoiceNumber(payment);
  const invoiceDate = payment.created_at ? new Date(payment.created_at) : new Date();
  const totalLabel = formatAmount(payment.amount);
  const customerName = payment.customer_name || 'Customer';
  const customerEmail = payment.customer_email || '';

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.text(INVOICE_COMPANY.legalName, margin, y);
  y += 14;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.text(INVOICE_COMPANY.addressLine1, margin, y);
  y += 12;
  doc.text(INVOICE_COMPANY.addressLine2, margin, y);
  y += 12;
  doc.setTextColor(37, 99, 235);
  doc.text(INVOICE_COMPANY.email, margin, y);
  doc.setTextColor(0, 0, 0);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(22);
  doc.text('INVOICE', pageWidth - margin, margin + 4, { align: 'right' });

  y += 18;
  doc.setDrawColor(17, 24, 39);
  doc.setLineWidth(1.5);
  doc.line(margin, y, pageWidth - margin, y);
  y += 22;

  const colMid = margin + 180;
  const colRight = pageWidth - margin - 150;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(8);
  doc.setTextColor(107, 114, 128);
  doc.text('BILL TO', margin, y);
  doc.text('SHIP TO', colMid, y);
  y += 12;

  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(17, 24, 39);
  doc.text(customerName, margin, y);
  doc.text(customerName, colMid, y);
  y += 12;
  if (customerEmail) {
    doc.text(customerEmail, margin, y);
    doc.text(customerEmail, colMid, y);
    y += 12;
  }

  const detailsX = pageWidth - margin;
  let detailsY = y - (customerEmail ? 24 : 12);
  const detailRows = [
    ['INVOICE #', invoiceNumber],
    ['DATE', formatShortDate(invoiceDate)],
    ['DUE DATE', computeDueDate(invoiceDate, isPaid)],
    ['TERMS', isPaid ? 'Paid' : 'Net 15'],
  ];

  doc.setFontSize(9);
  detailRows.forEach(([label, value]) => {
    doc.setFont('helvetica', 'bold');
    doc.text(label, colRight, detailsY);
    doc.setFont('helvetica', 'normal');
    doc.text(String(value), detailsX, detailsY, { align: 'right' });
    detailsY += 14;
  });

  y = Math.max(y, detailsY) + 16;

  const lineItems = buildInvoiceLineItems(payment);
  autoTable(doc, {
    startY: y,
    margin: { left: margin, right: margin },
    head: [['Date', 'Activity', 'Description', 'Qty', 'Rate', 'Amount']],
    body: lineItems.map((row) => [
      row.date,
      row.activity,
      row.description,
      String(row.qty),
      row.rate,
      row.amount,
    ]),
    styles: { fontSize: 9, cellPadding: 6 },
    headStyles: {
      fillColor: [255, 255, 255],
      textColor: [55, 65, 81],
      fontStyle: 'bold',
      lineWidth: { bottom: 1.5 },
      lineColor: [17, 24, 39],
    },
    columnStyles: {
      3: { halign: 'right' },
      4: { halign: 'right' },
      5: { halign: 'right' },
    },
    theme: 'plain',
  });

  y = doc.lastAutoTable.finalY + 24;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('Ways to pay', margin, y);
  doc.setFont('helvetica', 'normal');
  doc.text(
    isPaid ? 'Payment received — thank you.' : 'Pay online using your receipt link.',
    margin,
    y + 14
  );

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.text('BALANCE DUE', detailsX, y, { align: 'right' });
  doc.setFontSize(16);
  doc.text(isPaid ? '$0.00' : totalLabel, detailsX, y + 16, { align: 'right' });

  if (isPaid) {
    doc.setFontSize(8);
    doc.setTextColor(4, 120, 87);
    doc.text(`PAID ${totalLabel}`, detailsX, y + 34, { align: 'right' });
    doc.setTextColor(0, 0, 0);
  }

  const pageHeight = doc.internal.pageSize.getHeight();
  doc.setFontSize(8);
  doc.setTextColor(156, 163, 175);
  doc.text('— 1 of 1 —', pageWidth / 2, pageHeight - 28, { align: 'center' });

  doc.save(filename);
  return true;
}

/**
 * Open receipt in a new window and trigger print (Save as PDF in browser dialog).
 */
export function printReceiptDocument(rootSelector = '.vps-receipt-print-root') {
  const root = document.querySelector(rootSelector);
  if (!root) {
    window.print();
    return;
  }

  const printWindow = window.open('', '_blank', 'noopener,noreferrer,width=900,height=700');
  if (!printWindow) {
    window.print();
    return;
  }

  const styles = Array.from(document.querySelectorAll('link[rel="stylesheet"], style'))
    .map((node) => node.outerHTML)
    .join('\n');

  printWindow.document.write(`<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>VPS Invoice</title>
  ${styles}
  <style>
    body { margin: 0; padding: 1.5rem; background: #fff; }
    .vps-receipt-no-print { display: none !important; }
    .vps-receipt-print-root { max-width: 52rem; margin: 0 auto; }
    .vps-invoice-page { box-shadow: none; border: none; }
  </style>
</head>
<body>${root.outerHTML}</body>
</html>`);
  printWindow.document.close();
  printWindow.focus();
  setTimeout(() => {
    printWindow.print();
  }, 400);
}

/**
 * Download receipt as a standalone HTML invoice file.
 */
export function downloadReceiptHtml(rootSelector = '.vps-receipt-print-root', filename = 'vps-invoice.html') {
  const root = document.querySelector(rootSelector);
  if (!root) return false;

  const html = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="utf-8" />
  <meta name="viewport" content="width=device-width, initial-scale=1" />
  <title>VPS Invoice</title>
  <link rel="stylesheet" href="https://fonts.googleapis.com/css2?family=Arial&display=swap" />
  <style>
    body { font-family: Arial, Helvetica, sans-serif; margin: 2rem; color: #111827; background: #f3f4f6; }
    .vps-receipt-no-print { display: none !important; }
    .vps-invoice-page { max-width: 52rem; margin: 0 auto; background: #fff; border: 1px solid #e5e7eb; }
    .vps-invoice-header { display: flex; justify-content: space-between; padding: 1.5rem 2rem 1rem; border-bottom: 2px solid #111827; }
    .vps-invoice-title { margin: 0; font-size: 1.75rem; letter-spacing: 0.04em; }
    .vps-invoice-meta-grid { display: flex; flex-wrap: wrap; gap: 1rem; padding: 1.25rem 2rem; }
    .vps-invoice-address-label { font-size: 0.6875rem; font-weight: 700; color: #6b7280; margin-bottom: 0.35rem; }
    .vps-invoice-details { margin-left: auto; min-width: 12rem; }
    .vps-invoice-detail-row { display: flex; justify-content: space-between; font-size: 0.75rem; padding: 0.1rem 0; }
    .vps-invoice-table { width: 100%; border-collapse: collapse; font-size: 0.8125rem; }
    .vps-invoice-table th { text-align: left; border-bottom: 2px solid #111827; padding: 0.5rem; font-size: 0.6875rem; }
    .vps-invoice-table td { border-bottom: 1px solid #e5e7eb; padding: 0.5rem; }
    .num { text-align: right; }
    .vps-invoice-footer { display: flex; justify-content: space-between; padding: 1rem 2rem; border-top: 1px solid #e5e7eb; }
    .vps-invoice-balance { text-align: right; }
    .vps-invoice-balance-value { font-size: 1.375rem; font-weight: 700; }
    .vps-invoice-page-no { text-align: center; font-size: 0.6875rem; color: #9ca3af; padding: 0.5rem; }
  </style>
</head>
<body>${root.innerHTML}</body>
</html>`;

  const blob = new Blob([html], { type: 'text/html;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
  return true;
}
